// Externals
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Libraries
import {
    SEND_TO_DESK_VISIBILITY,
    isDealUpdating,
    updateActiveDealVisibilityStatus,
    updateDealSummary,
    updateNotes
} from '@makemydeal/dr-sales-checkin-store';

// Constants
import { SEND_TO_DESK_NOTE } from '../constants';

/**
 * Behaviour for the top-nav "Send to Desk" button - the same send the Deal
 * Summary "Send Request" button performs.
 *
 * `isLoading` is any deal save in flight, which is what blocks a second submission. `isSubmitting`
 * is this button's own send: every save in the app goes through the same request (adding a vehicle
 * from Browse Inventory included), so the pending label has to be attributed to the click rather
 * than to `isLoading`, or an unrelated save makes the button read as submitting.
 */
export const useSendToDesk = () => {
    const dispatch = useDispatch();
    const isLoading = useSelector(isDealUpdating);
    const [isSendToDeskClicked, setisSendToDeskClicked] = useState(false);

    // Release the attribution once that save has settled — and immediately if the click never
    // started one, so a later unrelated save can never inherit the pending label.
    useEffect(() => {
        if (isSendToDeskClicked && !isLoading) {
            setisSendToDeskClicked(false);
        }
    }, [isSendToDeskClicked, isLoading]);

    const onSendToDeskClick = useCallback(() => {
        setisSendToDeskClicked(true);
        dispatch(updateNotes(SEND_TO_DESK_NOTE));
        dispatch(updateActiveDealVisibilityStatus(SEND_TO_DESK_VISIBILITY));
        dispatch(updateDealSummary({ sentToDesk: true }));
    }, [dispatch]);

    return { isLoading, isSendToDeskClicked, onSendToDeskClick };
};
